import React, { useMemo, useState } from "react";
import logo from "assets/images/login-image.png";
import Input from "components/common/Input";
import Button from "components/common/Button";
import api from "config/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { setUserData } from "store/slices/main.slice";
import { IconButton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useCachedImage } from "hooks/useCachedImage";
import { resolveUploadAssetUrl } from "utils/media";

const Login = () => {
  const cart = useAppSelector(({ main }) => main.cart);
  const companyData = useAppSelector(({ main }) => main.companyData);
  const settings = useAppSelector(({ main }) => main.settings);
  const name = useAppSelector(
    ({ main }) => main.companyData?.companyName || "RANGER POS"
  );
  const resolvedLogoUrl = useMemo(
    () =>
      settings?.showLogo !== 0 && companyData?.logoUrl
        ? resolveUploadAssetUrl(companyData.logoUrl)
        : logo,
    [companyData?.logoUrl, settings?.showLogo]
  );
  const logoUrl = useCachedImage(resolvedLogoUrl, { cacheKey: "company-logo" });
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState({
    emailId: "",
    password: "",
  });

  const handleSubmit = () => {
    toast.dismiss();
    if (!data.emailId) {
      toast.error("Email is required");
      return;
    }
    if (!data.password) {
      toast.error("Password is required");
      return;
    }
    setLoading(true);
    api(`/api/Customer/ByIdPass?${new URLSearchParams(data)}`)
      .then(async ({ data }) => {
        for (let item of cart) {
          await api.post("/api/Cart/AddCart", [
            { ...item, id: 0, userId: data.clientId },
          ]);
        }
        dispatch(setUserData(data));
        toast.success("Sign In Successfully");
        navigate("/");
      })
      .catch((e) => {
        toast.error("Invalid Email or Password");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.34),_transparent_42%),linear-gradient(135deg,rgba(255,255,255,0.12),transparent_55%)]" />

      <div
        className="relative w-full max-w-5xl overflow-hidden rounded-[30px] border border-white/55 shadow-[0_28px_80px_rgba(15,23,42,0.18)]"
        style={{
          background: "linear-gradient(135deg, rgb(255 255 255 / 0.92), rgb(255 255 255 / 0.84))",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          fontFamily: '"Noto Sans", sans-serif',
        }}
      >
        <div className="grid md:grid-cols-[1.05fr_0.95fr]">
          <div className="relative hidden md:flex min-h-[560px] items-center justify-center px-10 py-10">
            <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(255,255,255,0.28),rgba(var(--color-primary),0.08))]" />
            <IconButton
              onClick={() => navigate("/")}
              className="!absolute !left-6 !top-6 !bg-white/80"
            >
              <ArrowBack />
            </IconButton>
            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="rounded-[28px] bg-white/70 px-6 py-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
                <img
                  key={logoUrl}
                  src={logoUrl}
                  className="h-[220px] w-[220px] object-contain lg:h-[280px] lg:w-[280px]"
                  alt="logo"
                />
              </div>
              <div className="mt-8 max-w-[360px]">
                <p className="text-[13px] font-[700] uppercase tracking-[0.18em] text-primary/80">
                  Welcome Back
                </p>
                <h2 className="mt-3 text-[34px] font-[900] leading-[1.05] tracking-[-0.04em] text-slate-900">
                  Sign in to continue your order.
                </h2>
                <p className="mt-3 text-[15px] leading-6 text-slate-600">
                  Access your saved details faster and keep your cart synced with your account.
                </p>
              </div>
            </div>
          </div>

          <div className="relative px-5 py-6 sm:px-8 md:px-10 md:py-10">
            <div className="md:hidden mb-4 flex items-center justify-between">
              <IconButton onClick={() => navigate("/")} className="!bg-white/80">
                <ArrowBack />
              </IconButton>
              <img key={logoUrl} src={logoUrl} className="h-14 object-contain" alt="logo" />
              <div className="w-10" />
            </div>

            <div className="mx-auto w-full max-w-[390px]">
              <div className="rounded-[24px] border border-white/65 bg-white/76 p-5 shadow-[0_18px_42px_rgba(15,23,42,0.08)] sm:p-6">
                <p className="text-[12px] font-[700] uppercase tracking-[0.16em] text-primary/80">
                  Account Login
                </p>
                <h1 className="mt-2 text-[30px] font-[900] leading-[1.05] tracking-[-0.04em] text-slate-900">
                  Sign in
                </h1>
                <p className="mt-3 text-[14px] leading-6 text-slate-600">
                  Continue with your {name} account to complete checkout faster.
                </p>

                <div className="mt-6 flex flex-col gap-4">
                  <Input
                    size="small"
                    label="Email"
                    variant="outlined"
                    value={data.emailId}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        backgroundColor: "rgba(255,255,255,0.78)",
                      },
                    }}
                    onValueChange={(emailId) => setData((e) => ({ ...e, emailId }))}
                  />
                  <Input
                    size="small"
                    label="Password"
                    type="password"
                    variant="outlined"
                    value={data.password}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        backgroundColor: "rgba(255,255,255,0.78)",
                      },
                    }}
                    onValueChange={(password) => setData((e) => ({ ...e, password }))}
                  />
                </div>

                <Button
                  disabled={loading}
                  className="!mt-6 !w-full !rounded-[16px] !py-3.5 !text-[15px] !font-[800]"
                  onClick={handleSubmit}
                >
                  Sign in
                </Button>

                <p className="mt-5 text-center text-[14px] text-slate-600">
                  Don&apos;t have an account?{" "}
                  <a href="/register" className="font-bold text-primary">
                    Register
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Login;
